import { DateSchedule, UpcomingAppointment } from "../types/appointment";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useState, useRef } from "react";

interface DateSelectorProps {
  dates: DateSchedule[];
  selectedDate: DateSchedule;
  onDateChange: (date: DateSchedule) => void;
  upcomingAppointments: UpcomingAppointment[];
  appointmentCountDate: [
    {
      date: string;
      count: number;
    }
  ];
}

export function DateSelector({
  dates,
  selectedDate,
  onDateChange,
  appointmentCountDate,
}: DateSelectorProps) {
  const [startIndex, setStartIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const handlePrevious = () => {
    if (startIndex > 0) {
      setStartIndex((prev) => Math.max(0, prev - 1));
    }
  };

  const handleNext = () => {
    if (startIndex < dates.length - 10) {
      setStartIndex((prev) => Math.min(dates.length - 10, prev + 1));
    }
  };

  return (
    <div className="border-b sticky top-16 z-10 bg-white w-full">
      <div className="relative flex items-center">
        {/* Left Arrow */}
        <button
          className={`absolute left-2 z-10 p-2 rounded-full bg-white shadow-md transition-colors
            ${
              startIndex === 0
                ? "text-gray-300 cursor-not-allowed"
                : "hover:bg-gray-50 text-gray-600"
            }`}
          onClick={handlePrevious}
          disabled={startIndex === 0}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        {/* Dates Container */}
        <div ref={containerRef} className="flex-1 overflow-hidden px-16 py-2">
          <div
            className="flex transition-transform duration-300 ease-in-out"
            style={{
              width: `${(dates.length / 10) * 100}%`,
              transform: `translateX(-${startIndex * (100 / dates.length)}%)`,
            }}
          >
            <div className="flex w-full">
              {dates.map((date) => (
                <button
                  key={date.fullDate}
                  onClick={() => onDateChange(date)}
                  className={`flex-1 rounded-xl group mx-1.5 relative px-4 py-2 transition-colors
                    ${
                      selectedDate.fullDate === date.fullDate
                        ? "bg-[#AD4F7C]/10 text-[#771D51]"
                        : "hover:bg-gray-400/20 bg-gray-100"
                    }
                  `}
                >
                  <div className="flex items-center flex-col gap-1">
                    <span
                      className={`text-sm tracking-wide ${
                        selectedDate.fullDate === date.fullDate &&
                        "font-extrabold"
                      }`}
                    >
                      {date.day}
                    </span>
                    <div className="flex items-center gap-1.5 justify-center">
                      <span
                        className={`text-2xl ${
                          selectedDate.fullDate === date.fullDate
                            ? "font-extrabold"
                            : "font-medium"
                        }`}
                      >
                        {date.date}
                      </span>

                      {appointmentCountDate?.find(
                        (item) => item.date === date.fullDate
                      ) && (
                        <span
                          className={`absolute bottom-1 right-1 bg-emerald-400 text-white rounded-full 
                                     px-2 py-0.5 text-xs leading-none 
                                     flex items-center justify-center 
                                     min-w-[20px] h-5`}
                        >
                          {
                            appointmentCountDate?.find(
                              (item) => item.date === date.fullDate
                            )?.count
                          }
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right Arrow */}
        <button
          className={`absolute right-2 z-10 p-2 rounded-full bg-white shadow-md transition-colors
            ${
              startIndex >= dates.length - 10
                ? "text-gray-300 cursor-not-allowed"
                : "hover:bg-gray-50 text-gray-600"
            }`}
          onClick={handleNext}
          disabled={startIndex >= dates.length - 10}
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
